import api from "./axios";

export interface StockArticle {
  id: number;
  name: string;
  unit: string;
  defaultSellingPrice: string;
  defaultSupplierPrice: string;
  minLevel: number;
  active: boolean;
  onHand: number;
}

export interface StockMovement {
  id: number;
  articleId: number;
  type: "IN" | "OUT" | "ADJUST";
  quantity: number;
  refType?: string;
  refId?: string;
  isNegativeOverride: boolean;
  agentId: number;
  responsibleAgentId?: number;
  createdAt: string;
}

export type StockEditTargetType = "article_fields" | "stock_adjustment";

export interface StockArticleFieldChanges {
  name?: string;
  unit?: string;
  defaultSellingPrice?: number;
  defaultSupplierPrice?: number;
  minLevel?: number;
}

export interface StockAdjustmentChanges {
  quantity: number;
}

export type StockEditProposedChanges = StockArticleFieldChanges | StockAdjustmentChanges;

export interface StockEditRequest {
  id: number;
  targetType: StockEditTargetType;
  stockArticleId: number;
  requestedBy: number;
  reason: string;
  proposedChanges: StockEditProposedChanges;
  status: "pending" | "approved" | "rejected";
  reviewedBy?: number;
  reviewedAt?: string;
  reviewNote?: string;
  createdAt: string;
}

export const stockApi = {
  list: (includeInactive = false) =>
    api.get<StockArticle[]>("/stock", { params: { includeInactive } }),
  getById: (id: number) => api.get<StockArticle>(`/stock/${id}`),
  create: (data: {
    name: string;
    unit?: string;
    defaultSellingPrice: number;
    defaultSupplierPrice?: number;
    minLevel?: number;
  }) => api.post<StockArticle>("/stock", data),
  update: (
    id: number,
    data: Partial<{
      name: string;
      unit: string;
      defaultSellingPrice: number;
      defaultSupplierPrice: number;
      minLevel: number;
    }>,
  ) => api.patch<StockArticle>(`/stock/${id}`, data),
  toggleActive: (id: number, active: boolean) =>
    api.patch<StockArticle>(`/stock/${id}/active`, { active }),
  restock: (id: number, type: "IN" | "ADJUST", quantity: number, responsibleAgentId?: number) =>
    api.post<StockMovement>(`/stock/${id}/restock`, { type, quantity, responsibleAgentId }),
  listMovements: (id: number) => api.get<StockMovement[]>(`/stock/${id}/movements`),
  reassignMovementResponsibleAgent: (movementId: number, responsibleAgentId: number, reason?: string) =>
    api.patch<StockMovement>(`/stock/movements/${movementId}/responsible-agent`, {
      responsibleAgentId,
      reason,
    }),
  remove: (id: number) => api.delete<StockArticle>(`/stock/${id}`),
  restore: (id: number) => api.post<StockArticle>(`/stock/${id}/restore`),
  trash: () => api.get<StockArticle[]>("/stock/trash"),

  // Modification-request workflow — see stock-edit.service.ts server-side for
  // why managers submit a request instead of editing / adjusting directly.
  requestEdit: (
    articleId: number,
    targetType: StockEditTargetType,
    reason: string,
    proposedChanges: StockEditProposedChanges,
  ) =>
    api.post<StockEditRequest>(`/stock/${articleId}/edit-requests`, {
      targetType,
      reason,
      proposedChanges,
    }),
  listEditRequests: (status: "pending" | "approved" | "rejected" = "pending") =>
    api.get<StockEditRequest[]>("/stock/edit-requests", { params: { status } }),
  listAllEditRequests: async () => {
    const [pending, approved, rejected] = await Promise.all([
      stockApi.listEditRequests("pending"),
      stockApi.listEditRequests("approved"),
      stockApi.listEditRequests("rejected"),
    ]);
    return {
      data: [...pending.data, ...approved.data, ...rejected.data].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    };
  },
  approveEditRequest: (requestId: number) =>
    api.post<StockEditRequest>(`/stock/edit-requests/${requestId}/approve`),
  rejectEditRequest: (requestId: number, reviewNote?: string) =>
    api.post<StockEditRequest>(`/stock/edit-requests/${requestId}/reject`, { reviewNote }),
};
